<script lang="ts" setup>
  import { phonePortraitOutline } from 'ionicons/icons'
  import { z } from 'zod'
  import { PHONE_NUM_LENGTH } from '~/constants/profile'
  import { PHONE_NUMBER_ERROR } from '~/constants/errorMessage'
  import { mobileNumberRegex } from '~/constants/regex'
  import { useMobileVerificationModal } from '~/composables/verificationModal'
  import { gaEventVerificationMobileSubmit } from '~/composables/analytics'
  import { updateMobile } from '~/composables/api'
  import type { ViewSource } from '~/types/route'
  import { useNativeHaptics } from '~/composables/nativeHaptics'
  import { useAuthStore, useUserStore } from '~/store'

  const { showMobileVerificationModal, mobileVerificationModalId } = useMobileVerificationModal()
  const authStore = useAuthStore()
  const { isWebMode } = useAppMode()
  const userStore = useUserStore()

  const props = defineProps<{
    verificationModalId: string
    source: ViewSource
    initialValue?: string | null
    isVerified?: boolean | null
  }>()

  const emit = defineEmits<{
    (
      event: 'alert',
      alertContent: {
        header: string
        subHeader?: string
        message: string
      }
    ): void
    (event: 'routing'): void
    (event: 'verification-success'): void
  }>()

  const validationSchema = toTypedSchema(
    z.object({
      mobileNumber: z
        .string()
        .regex(mobileNumberRegex, PHONE_NUMBER_ERROR.INVALID)
        .length(PHONE_NUM_LENGTH, { message: PHONE_NUMBER_ERROR.LENGTH })
    })
  )

  const { handleSubmit, errors, isSubmitting, meta, values, resetField } = useForm({
    validationSchema,
    initialValues: {
      mobileNumber: props.initialValue ?? ''
    }
  })

  /**
   * Form is valid if:
   * - Form is valid (vee-validate)
   * - No errors (vee-validate)
   */
  const isFormValid = computed(() => {
    return meta.value.valid && !Object.keys(errors.value).length
  })

  const isFormDirty = computed(() => {
    return meta.value.dirty
  })

  const isCtaDisabled = computed<boolean>(() => {
    return !isFormValid.value || (props.isVerified === true && !isFormDirty.value)
  })

  const isCurrentValueVerified = computed<boolean>(() => {
    if (values.mobileNumber === props.initialValue) {
      return props.isVerified ?? false
    }
    return false
  })

  watch(
    () => props.initialValue,
    (newValue) => {
      // Reset the field when the initial value changes, to reset the dirty flag
      resetField('mobileNumber', { value: newValue ?? '' })
    }
  )

  const onSubmit = handleSubmit(async () => {
    isSubmitting.value = true

    try {
      if (!isFormValid.value) {
        await useNativeHaptics().triggerVibrate()
        emit('alert', {
          header: 'Oops. Unexpected Error.',
          message: 'Please check your connection or try again later.'
        })
        console.warn('Email value is invalid', errors)
        return
      }

      const rawValues = toRaw(values)
      await updateMobile({
        mobileNumber: rawValues?.mobileNumber ?? ''
      })

      openVerificationModal()

      gaEventVerificationMobileSubmit(props.source)
    } catch (error: any) {
      await useNativeHaptics().triggerVibrate()
      var message = getApiErrorMessage(error) ?? 'Please check your connection or try again later.'
      var title = getApiErrorTitle(error) ?? 'Oops. Unexpected Error.'
      emit('alert', {
        header: title,
        message
      })
      console.warn('Error sending Verification message', error)
    } finally {
      isSubmitting.value = false
    }
  })

  const openVerificationModal = async () => {
    const rawValues = toRaw(values)
    await showMobileVerificationModal({
      source: props.source,
      title: 'Verify Mobile',
      message: rawValues?.mobileNumber
        ? `Enter the code we’ve sent to your phone number ending in ${rawValues?.mobileNumber.slice(-4)}`
        : 'Enter the code we’ve sent to your phone',
      showPasswordInput: true,
      onSubmit: onOtpSubmit,
      onSuccessfulVerifyCode: () => onSuccessfulVerifyCode(),
      onSignOut: () => handleSignOut(),
      onDismiss: () => {}
    })
  }

  async function onOtpSubmit(verificationCode: string, password?: string) {
    const accountDetails = await confirmMobile({
      code: verificationCode,
      password: password ?? ''
    })

    gaEventMobileVerificationSuccess(props.source)
    authStore.signIn(accountDetails.session!, accountDetails.user!)
  }

  async function onSuccessfulVerifyCode() {
    await modalController.dismiss(undefined, undefined, mobileVerificationModalId)
    emit('verification-success')
  }

  function handleSignOut() {
    if (isWebMode) {
      emitDidSignOutFromWebsite()
    }
    userStore.hasOnboarded = false

    // Dismiss modals
    modalController.dismiss(undefined, undefined, mobileVerificationModalId)
    modalController.dismiss(undefined, undefined, props.verificationModalId)

    emit('routing')
    authStore.signOut()
  }
</script>

<template>
  <form class="verification-form">
    <div class="verification-container">
      <div class="verification-text" :class="{ verified: isCurrentValueVerified }">
        {{ isCurrentValueVerified ? 'Verified' : 'Unverified' }}
      </div>
    </div>
    <div class="input-with-helper-text">
      <GInputField
        label="Mobile Number"
        :max-length="100"
        input-mode="tel"
        name="mobileNumber"
        type="tel"
        clear-input
      />
    </div>
    <GButton
      class="button"
      :disabled="isCtaDisabled"
      label="Verify Mobile Number"
      :should-show-loading-animation="isSubmitting"
      :icon="phonePortraitOutline"
      show-icon-lhs
      animation-color="light"
      block
      variant="primary"
      @click.prevent.stop="onSubmit"
    />
  </form>
</template>

<style lang="scss" scoped>
  .verification-form {
    background-color: white;
    padding: 16px;
    align-self: stretch;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    border-radius: 0px 0px 16px 16px;
    border-bottom: 1px solid var(--color-secondary-paper-light, #f5f2ef);
    background: white;

    .verification-container {
      display: flex;
      flex-direction: row;
      justify-content: left;
      align-items: flex-start;
      gap: 8px;
      width: 100%;

      .verification-text {
        @include typography-body-small-strong;
        align-self: stretch;
        color: var(--color-base-red);

        &.verified {
          color: var(--color-alert-success);
        }
      }
    }
    .input-with-helper-text {
      display: flex;
      flex-direction: column;
      row-gap: 8px;
      width: 100%;
    }

    .button {
      width: 100%;
    }
  }
</style>
